/**
 * GULLYO lightweight predictive search — products only, debounced, abortable.
 */
import { buildProductSearchUrl, buildProductTitleQuery } from '@theme/gullyo-search-query';

(function () {
  const MIN_CHARS = 2;
  const DEBOUNCE_MS = 180;
  const SECTION_ID = 'gullyo-predictive-search';
  const CACHE_MAX = 15;

  /** @type {AbortController | null} */
  let searchController = null;
  /** @type {number | null} */
  let debounceTimer = null;
  /** @type {number} */
  let requestId = 0;
  /** @type {Map<string, string>} */
  const searchCache = new Map();

  /** @type {HTMLElement | null} */
  let panel = null;
  /** @type {HTMLInputElement | null} */
  let input = null;
  /** @type {HTMLFormElement | null} */
  let form = null;
  /** @type {HTMLElement | null} */
  let dialog = null;
  /** @type {number} */
  let activeIndex = -1;

  function getRoutes() {
    const themeRoutes = window.Theme?.routes;
    if (themeRoutes?.predictive_search_url && themeRoutes?.search_url) {
      return themeRoutes;
    }
    return {
      predictive_search_url: '/search/suggest',
      search_url: '/search',
    };
  }

  function normalizeQuery(value) {
    return value.trim().toLowerCase();
  }

  function normalizeHandle(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function cacheSet(key, html) {
    if (searchCache.has(key)) {
      searchCache.delete(key);
    }
    searchCache.set(key, html);
    if (searchCache.size > CACHE_MAX) {
      const oldest = searchCache.keys().next().value;
      searchCache.delete(oldest);
    }
  }

  function getItems() {
    if (!panel) return [];
    return Array.from(panel.querySelectorAll('[data-gullyo-search-suggest-item]'));
  }

  function setExpanded(expanded) {
    if (!input) return;
    input.setAttribute('aria-expanded', expanded ? 'true' : 'false');
  }

  function setActiveItem(index) {
    const items = getItems();
    activeIndex = index;
    items.forEach((item, i) => {
      const selected = i === index;
      item.setAttribute('aria-selected', selected ? 'true' : 'false');
      item.classList.toggle('is-active', selected);
    });

    if (input) {
      const activeItem = activeIndex >= 0 ? items[activeIndex] : null;
      if (activeItem?.id) {
        input.setAttribute('aria-activedescendant', activeItem.id);
      } else {
        input.removeAttribute('aria-activedescendant');
      }
    }
  }

  function findExactMatchItem(query) {
    const queryHandle = normalizeHandle(query);
    if (!queryHandle) return null;

    return (
      getItems().find((item) => {
        const title = normalizeHandle(item.dataset.gullyoSearchSuggestTitle || '');
        const handle = normalizeHandle(item.dataset.gullyoSearchSuggestHandle || '');
        let hrefHandle = '';

        try {
          const url = new URL(item.href, window.location.origin);
          hrefHandle = normalizeHandle(url.pathname.split('/').filter(Boolean).pop() || '');
        } catch (_error) {
          hrefHandle = '';
        }

        return title === queryHandle || handle === queryHandle || hrefHandle === queryHandle;
      }) || null
    );
  }

  function showPanel() {
    if (!panel) return;
    panel.hidden = false;
    setExpanded(true);
  }

  function hidePanel() {
    if (!panel) return;
    panel.hidden = true;
    panel.innerHTML = '';
    setExpanded(false);
    setActiveItem(-1);
  }

  function showLoading() {
    if (!panel) return;
    showPanel();
    panel.innerHTML = `
      <div class="gullyo-search-suggest gullyo-search-suggest--loading" aria-hidden="true">
        <p class="gullyo-search-suggest__status">SEARCHING…</p>
        <div class="gullyo-search-suggest__skeleton" aria-hidden="true">
          <span></span><span></span><span></span>
        </div>
      </div>
    `;
    setActiveItem(-1);
  }

  function renderResults(html, query) {
    if (!panel) return;

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const root =
      doc.querySelector('[data-gullyo-search-suggest-results]') ||
      doc.querySelector('.gullyo-search-suggest');

    if (!root) {
      panel.innerHTML = `
        <div class="gullyo-search-suggest">
          <p class="gullyo-search-suggest__empty">No products found for &ldquo;${escapeHtml(query)}&rdquo;</p>
          <a href="${buildSearchUrl(query)}" class="gullyo-search-suggest__view-all" data-gullyo-search-suggest-view-all>VIEW ALL RESULTS</a>
        </div>
      `;
      showPanel();
      setActiveItem(-1);
      return;
    }

    panel.innerHTML = root.outerHTML;
    const viewAllLink = panel.querySelector('[data-gullyo-search-suggest-view-all]');
    if (viewAllLink instanceof HTMLAnchorElement) {
      viewAllLink.href = buildSearchUrl(query);
    }
    showPanel();
    setActiveItem(-1);
  }

  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function buildSearchUrl(query) {
    return buildProductSearchUrl(getRoutes().search_url, query);
  }

  function abortActiveRequest() {
    if (searchController) {
      searchController.abort();
      searchController = null;
    }
  }

  function clearSuggestions() {
    abortActiveRequest();
    if (debounceTimer) {
      clearTimeout(debounceTimer);
      debounceTimer = null;
    }
    requestId += 1;
    hidePanel();
  }

  async function fetchSuggestions(query) {
    const normalized = normalizeQuery(query);
    if (normalized.length < MIN_CHARS) {
      clearSuggestions();
      return;
    }

    const productQuery = buildProductTitleQuery(normalized);
    if (!productQuery) {
      clearSuggestions();
      return;
    }

    if (searchCache.has(normalized)) {
      if (!input || normalizeQuery(input.value) !== normalized) return;
      renderResults(searchCache.get(normalized), query);
      return;
    }

    abortActiveRequest();
    searchController = new AbortController();
    const currentRequestId = ++requestId;
    const signal = searchController.signal;

    showLoading();

    const url = new URL(getRoutes().predictive_search_url, window.location.origin);
    url.searchParams.set('q', productQuery);
    url.searchParams.set('resources[type]', 'product');
    // Request one spare result because internal merchandising resources are removed server-side.
    url.searchParams.set('resources[limit]', '7');
    url.searchParams.set('resources[options][unavailable_products]', 'hide');
    url.searchParams.set('section_id', SECTION_ID);

    try {
      const response = await fetch(url.toString(), {
        signal,
        headers: {
          Accept: 'text/html',
        },
      });

      if (!response.ok) {
        throw new Error(`Predictive search failed: ${response.status}`);
      }

      const html = await response.text();
      if (signal.aborted || currentRequestId !== requestId) return;
      if (!input || normalizeQuery(input.value) !== normalized) return;

      cacheSet(normalized, html);
      renderResults(html, query);
    } catch (error) {
      if (error?.name === 'AbortError') return;
      if (currentRequestId !== requestId) return;
      if (!panel) return;

      panel.innerHTML = `
        <div class="gullyo-search-suggest">
          <p class="gullyo-search-suggest__empty">No products found for &ldquo;${escapeHtml(query)}&rdquo;</p>
          <a href="${buildSearchUrl(query)}" class="gullyo-search-suggest__view-all" data-gullyo-search-suggest-view-all>VIEW ALL RESULTS</a>
        </div>
      `;
      showPanel();
    } finally {
      if (searchController?.signal === signal) {
        searchController = null;
      }
    }
  }

  function scheduleFetch(query) {
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    const trimmed = query.trim();
    if (trimmed.length < MIN_CHARS) {
      clearSuggestions();
      return;
    }

    debounceTimer = window.setTimeout(() => {
      debounceTimer = null;
      fetchSuggestions(trimmed);
    }, DEBOUNCE_MS);
  }

  function onInput() {
    if (!input) return;
    scheduleFetch(input.value);
  }

  function onKeyDown(event) {
    if (!input || !panel || panel.hidden) return;

    const items = getItems();
    if (!items.length) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      const next = activeIndex < items.length - 1 ? activeIndex + 1 : 0;
      setActiveItem(next);
      items[next]?.scrollIntoView({ block: 'nearest' });
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      const prev = activeIndex > 0 ? activeIndex - 1 : items.length - 1;
      setActiveItem(prev);
      items[prev]?.scrollIntoView({ block: 'nearest' });
      return;
    }

    if (event.key === 'Enter' && activeIndex >= 0 && items[activeIndex]) {
      event.preventDefault();
      window.location.href = items[activeIndex].href;
    }
  }

  function init() {
    if (window.__gullyoSearchSuggestInit) return;

    const modal = document.getElementById('search-modal');
    if (!modal) return;

    dialog = modal.querySelector('dialog');
    form = modal.querySelector('[data-gullyo-search-form]');
    input = modal.querySelector('[data-gullyo-search-input]');
    panel = modal.querySelector('[data-gullyo-search-suggestions]');

    if (!dialog || !form || !input || !panel) return;

    window.__gullyoSearchSuggestInit = true;
    window.__gullyoSearchSuggestClear = clearSuggestions;

    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-expanded', 'false');
    input.setAttribute('aria-controls', 'gullyo-search-suggest-list');

    input.addEventListener('input', onInput);
    input.addEventListener('keydown', onKeyDown);

    dialog.addEventListener('toggle', () => {
      if (!dialog.open) {
        clearSuggestions();
        input.value = '';
      }
    });

    document.addEventListener('gullyo:search-closed', clearSuggestions);

    form.addEventListener('submit', (event) => {
      const exactMatch = findExactMatchItem(input.value);

      if (exactMatch?.href) {
        event.preventDefault();
        clearSuggestions();
        window.location.href = exactMatch.href;
        return;
      }

      const query = input.value.trim();
      clearSuggestions();
      if (!query) return;

      event.preventDefault();
      window.location.href = buildSearchUrl(query);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
